import { Calendar, Clock } from '@untitled-ui/icons-react/build/cjs';
import React, { useMemo } from 'react';
import BadgeCmp from 'src/components/BadgeCmp';
import CardCmp from 'src/components/CardCmp';
import NotFoundUI from 'src/components/NotFoundUI';
import TruncateTextCmp from 'src/components/TruncateText';

import { useRadarData } from '../../hooks/useRadarData';
import { checkLocationUpdate } from '../../track.utils';

import { SHARE_MOBILE_CARDCMP } from './common/shareMobile.styles';

// isShareMobile: share-link mobile sheet (Updates tab) — Figma card styling. The tab already scrolls
// inside the sheet, so the list renders in full there instead of in its own 320px scroll box.
const LocationUpdatesSection: React.FC<{ isShareMobile?: boolean }> = ({ isShareMobile = false }) => {
    const { selectedLeg, isDetailLoading } = useRadarData();

    // Figma share-mobile: 14px #344054 icons, 12/18 regular #667085 date/time text.
    const iconClass = isShareMobile ? 'w-3.5 h-3.5 text-textSecondary' : 'w-4 h-4 text-grayLight600';
    const dateTextClass = isShareMobile ? 'font-normal text-gray500' : 'font-medium text-textSecondary';

    const sortedUpdates = useMemo(() => {
        const updates = selectedLeg?.locationUpdates ?? [];

        return [...updates].sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }, [selectedLeg]);

    return (
        <CardCmp
            headerTitle="Location Updates"
            // Share sheet: always open — no collapse chevron.
            isAccordion={!isShareMobile}
            isFooter={false}
            headerParentClassName={`!border-b-0 px-4 radar-card-title ${isShareMobile ? SHARE_MOBILE_CARDCMP.headerParentClassName : ''}`}
            cardClassName={isShareMobile ? SHARE_MOBILE_CARDCMP.cardClassName : '!border-0 !rounded-lg'}
            childrenClassName={isShareMobile ? `px-4 pb-3 ${SHARE_MOBILE_CARDCMP.childrenClassName}` : 'px-4 pb-3 overflow-auto custom-scrollbar-v2 max-h-[320px]'}
            isCardContentWithoutSpacing={true}
        >
            {isDetailLoading ? (
                <ul className={`flex flex-col ${isShareMobile ? 'gap-y-2' : 'gap-y-1.5'}`}>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <li key={i} className={`p-3 border border-utilityGray200 rounded-lg ${isShareMobile ? 'bg-utilityGray300' : 'bg-gray25'}`}>
                            <p className={`text-xs leading-[18px] font-medium w-fit custom-loading ${isShareMobile ? 'mb-0.5' : ''}`}>Harrisburg</p>
                            <p className="text-xs leading-[18px] w-fit custom-loading">17102, Pennsylvania, US</p>
                            <div className="border-t border-dashed border-utilityGray200 my-2" />
                            <p className="text-xs leading-[18px] w-fit custom-loading">June 30th 2026 3:41 PM</p>
                        </li>
                    ))}
                </ul>
            ) : sortedUpdates.length === 0 ? (
                <div className="flex flex-col h-fit min-h-[180px]">
                    <NotFoundUI
                        title="No Location Updates Found"
                        desc="Location updates will appear here as the shipment progresses."
                        containerClassName="!h-auto py-0 flex-1"
                        iconClassName="!hidden"
                        iconSecondaryClassName="!hidden"
                        isEmptyBackground={true}
                    />
                </div>
            ) : (
                <ul className={`flex flex-col ${isShareMobile ? 'gap-y-2' : 'gap-y-1.5'}`}>
                    {sortedUpdates.map((update: any, index: number) => {
                        const subtitle = [update.postal, update.state, update.countryCode].filter(Boolean).join(', ');
                        // A geocode failure on the backend (GoogleMap.getAddressFromCoordinates returns null
                        // on any Google error) stores the point with every address column NULL, which used to
                        // render as "-" over "-". carrierCurrentLocation / fullAddress are the provider's own
                        // strings and survive that failure, so fall back to them. Shown whole, never parsed for
                        // a city: the shape varies per provider (full address on Motive/Samsara, bare city on
                        // lboard), so splitting it would guess wrong. When the fallback supplies the title there
                        // is nothing left for the second line, so it is dropped rather than printing "-".
                        const fallbackLocation = update.carrierCurrentLocation || update.fullAddress || null;
                        const title = update.city ?? fallbackLocation ?? '-';
                        const subtitleText = subtitle || (update.city ? fallbackLocation : null);
                        // Date and time sit on separate items — split the same string at " at ", no format change.
                        // Relative values ("4 hours ago") have no " at " and render as a single clock item.
                        const [rawUpdatedDate, updatedTime] = checkLocationUpdate(update.createdAt, 'UTC').split(' at ');
                        // checkLocationUpdate returns mid-sentence casing because its other callers append it
                        // to a label ("Last location update yesterday at 6:29 PM"). Here the date stands alone
                        // in its own chip, so it reads as a sentence start. Capitalizing the first character
                        // covers every branch of that function at once rather than naming them one by one:
                        //   "just now" / "yesterday"        -> "Just now" / "Yesterday"   (lowercase, fixed)
                        //   "5 minutes ago" / "3 hours ago" -> unchanged                  (starts with a digit)
                        //   "Thursday" / "October 2nd 2026" -> unchanged                  (moment already caps)
                        //   "-"                             -> unchanged                  (no date on the row)
                        // and it keeps covering any lowercase branch added to it later.
                        const updatedDate = rawUpdatedDate.charAt(0).toUpperCase() + rawUpdatedDate.slice(1);

                        return (
                            <li key={update.id} className={`p-3 border border-utilityGray200 rounded-lg ${isShareMobile ? 'bg-utilityGray300' : 'bg-gray25'}`}>
                                {/* Top — city (+ Latest on the newest update) and address */}
                                <div className={`flex items-center gap-1.5 min-w-0 ${isShareMobile ? 'mb-0.5' : ''}`}>
                                    <TruncateTextCmp refName={{ current: null } as any} message={title}>
                                        <p className={`text-xs leading-[18px] font-medium truncate ${isShareMobile ? 'text-grayLight600' : 'text-grayLight900'}`}>{title}</p>
                                    </TruncateTextCmp>
                                    {index === 0 && (
                                        <BadgeCmp style="modern" type="success" mainClassName="bg-white flex-none">
                                            Latest
                                        </BadgeCmp>
                                    )}
                                </div>
                                {subtitleText && <p className="text-xs leading-[18px] font-normal text-grayLight600 truncate">{subtitleText}</p>}

                                <div className="border-t border-dashed border-utilityGray200 my-2" />

                                {/* Bottom — date + time. top-px on the text: Inter's line box puts digits/caps ~1px
                                    above centre, so the geometrically centred icon otherwise looks low. */}
                                <div className={`flex items-center flex-wrap ${isShareMobile ? 'gap-x-4' : 'gap-x-3'}`}>
                                    {updatedTime ? (
                                        <>
                                            <span className="flex items-center gap-x-1.5 h-[18px]">
                                                <Calendar className={`flex-none ${iconClass}`} />
                                                <span className={`relative top-px text-xs leading-[18px] ${dateTextClass}`}>{updatedDate}</span>
                                            </span>
                                            <span className="flex items-center gap-x-1.5 h-[18px]">
                                                <Clock className={`flex-none ${iconClass}`} />
                                                <span className={`relative top-px text-xs leading-[18px] ${dateTextClass}`}>{updatedTime}</span>
                                            </span>
                                        </>
                                    ) : (
                                        <span className="flex items-center gap-x-1.5 h-[18px]">
                                            <Clock className={`flex-none ${iconClass}`} />
                                            <span className={`relative top-px text-xs leading-[18px] ${dateTextClass}`}>{updatedDate}</span>
                                        </span>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </CardCmp>
    );
};

export default LocationUpdatesSection;
